import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, DragEvent, ChangeEvent, useRef } from 'react';
import _ from 'lodash';
import { IconFolderOpen } from '@tabler/icons-react';

// Used Mui's Button here instead of SegmentButton because of limitations with the component prop
// https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop

type BatchFileUploadInputProps = {
  batchFileMode: true;
  onBatchChange: (files: File[]) => void;
  onChange?: never;
};

type SingleFileUploadInputProps = {
  batchFileMode?: false;
  onChange: (file: File) => void;
  onBatchChange?: never;
};

type FileUploadInputProps = (
  | BatchFileUploadInputProps
  | SingleFileUploadInputProps
) & {
  defaultText?: string;
  acceptedExtensions?: string[];
  allowAllExtensions?: boolean;
  inlineVariant?: boolean;
  inlineVariantHeight?: 'sm' | 'md';
};

const FileUploadInput = ({
  onChange,
  onBatchChange,
  defaultText = 'Upload CSV, XLS, XLSX File',
  acceptedExtensions = ['csv', 'xlsx', 'xls'],
  allowAllExtensions,
  batchFileMode = false,
  inlineVariant = false,
  inlineVariantHeight = 'sm',
}: FileUploadInputProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [filetypeError, setFiletypeError] = useState<string | undefined>(
    undefined
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasBatchFiles, setHasBatchFiles] = useState(false);

  const resetFileInput = () => {
    if (containerRef.current) {
      const inputs =
        containerRef.current.querySelectorAll('input[type="file"]');
      inputs.forEach((input) => {
        (input as HTMLInputElement).value = '';
      });
    }
    // Reset batch files state when input is reset
    if (batchFileMode) {
      setHasBatchFiles(false);
    }
  };

  const handleFileChange = (newFile?: File) => {
    if (newFile) {
      setFileName(newFile.name);
      if (onChange) {
        onChange(newFile);
      }
      resetFileInput();
    }
  };

  const handleBatchFileChange = (newFiles?: File[]) => {
    if (newFiles) {
      // Clear fileName for batch mode since we're handling multiple files
      setFileName(null);
      setHasBatchFiles(true);
      if (onBatchChange) {
        onBatchChange(newFiles);
      }
      resetFileInput();
    }
  };

  const handleSetFiletypeError = () => {
    setFiletypeError(
      `Invalid file type. Please select a file with one of these extensions: ${_.chain(
        acceptedExtensions
      )
        .map((ext) => `.${ext}`)
        .join(', ')
        .value()}`
    );
  };

  const handleValidateFileType = (file: File) =>
    new RegExp(
      `([a-zA-Z0-9\\s_.\\-():])+(${_.chain(acceptedExtensions)
        .map((ext) => `.${ext}`)
        .join('|')
        .value()})$`,
      'i'
    ).test(file?.name ?? '');

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    setFiletypeError(undefined);
    setIsDragOver(false);
    e.preventDefault();
    if (batchFileMode) {
      const files = Array.from(e.dataTransfer.files);
      if (
        allowAllExtensions ||
        _.every(files, (file) => handleValidateFileType(file))
      ) {
        handleBatchFileChange(files);
      } else {
        handleSetFiletypeError();
      }
    } else {
      const firstFile = _.first(e.dataTransfer.files);
      if (
        allowAllExtensions ||
        (firstFile && handleValidateFileType(firstFile))
      ) {
        handleFileChange(firstFile);
      } else {
        handleSetFiletypeError();
      }
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFiletypeError(undefined);
    if (batchFileMode) {
      if (e.currentTarget.files) {
        const files = Array.from(e.currentTarget.files);
        if (
          allowAllExtensions ||
          _.every(files, (file) => handleValidateFileType(file))
        ) {
          handleBatchFileChange(files);
        } else {
          handleSetFiletypeError();
        }
      }
    } else {
      const firstFile = _.first(e.currentTarget.files);
      if (
        allowAllExtensions ||
        (firstFile && handleValidateFileType(firstFile))
      ) {
        handleFileChange(firstFile);
      } else {
        handleSetFiletypeError();
      }
    }
  };

  return (
    <Stack
      ref={containerRef}
      direction={inlineVariant ? 'row' : 'column'}
      alignItems="center"
      justifyContent="center"
      spacing={2}
      textAlign="center"
      sx={{
        border: inlineVariant ? '1px dashed' : '2px dashed',
        borderRadius: '5px',
        height: inlineVariant
          ? { sm: '32px', md: '50px' }[inlineVariantHeight]
          : '100%',
        width: '100%',
        padding: 1,
        backgroundColor: isDragOver ? 'action.hover' : 'transparent',
        transition: 'background-color 0.2s ease',
      }}
      onDrop={(e) => handleDrop(e)}
      onDragOver={(event) => event.preventDefault()}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        // Only set drag over to false if we're leaving the dropzone entirely
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsDragOver(false);
        }
      }}
    >
      <Typography
        sx={{ color: filetypeError ? 'red' : 'inherit' }}
        variant={
          inlineVariant && inlineVariantHeight === 'sm' ? 'caption' : undefined
        }
      >
        {filetypeError ??
          (batchFileMode && hasBatchFiles
            ? 'Files selected'
            : fileName ?? defaultText)}
      </Typography>
      {inlineVariant ? (
        <IconButton size="small" component="label">
          <IconFolderOpen color={theme.palette.primary.main} />
          <input
            type="file"
            onChange={onInputChange}
            style={{ display: 'none' }}
            accept={
              allowAllExtensions
                ? undefined
                : _.chain(acceptedExtensions)
                    .map((ext) => `.${ext}`)
                    .join(',')
                    .value()
            }
            multiple={batchFileMode}
          />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          component="label"
          startIcon={<IconFolderOpen />}
        >
          {batchFileMode ? 'Select Files' : 'Select File'}
          <input
            type="file"
            onChange={onInputChange}
            style={{ display: 'none' }}
            accept={
              allowAllExtensions
                ? undefined
                : _.chain(acceptedExtensions)
                    .map((ext) => `.${ext}`)
                    .join(',')
                    .value()
            }
            multiple={batchFileMode}
          />
        </Button>
      )}
    </Stack>
  );
};

export default FileUploadInput;
